import { contactMethodsToFormkit, personContactConfig, type ContactMethodsFormkit } from "@/components/contactMethods";
import { LE } from "@/constants/enums";
import type { EntityWithAudits } from "@modernary/mui/components/audit";
import { type PagingQuery } from "@modernary/mui/utils/api";
import type { EntityWithAuthRequestFields } from "@modernary/mui/utils/helpers";
import {
  plainMurnAddressFromFormkit,
  plainMurnAddressToFormkit,
  type AddressWithMurn,
  type HasPlainAddressWithMurn,
} from "@modernary/mui/components/fk";
import { formatAddressFields } from "@modernary/mui/utils/filters";

export interface ContactMethod {
  id?: number;
  ownerId?: number;
  kind?: LE.ContactMethodType | "email" | "phone" | "address";
  typeMurn?: string;
  entityMurn?: string;
  isPreferred?: boolean;
  /** `value` is the primary value of the contact method (only used by Email and Phone). */
  value?: string;
  street?: string;
  poBox?: string;
  city?: string;
  stateMurn?: string;
  zipCode?: string;
  countryMurn?: string;
  /** FormKit AddressInputLookup group; present only while editing address methods. */
  murnAddress?: AddressWithMurn;
}

export type ContactMethodCollection = ContactMethod[] | { _items?: ContactMethod[]; value?: ContactMethod[] };

export interface Person extends EntityWithAuthRequestFields, EntityWithAudits {
  id: number;
  murn?: string;
  firstName?: string;
  preferredName?: string;
  lastName?: string;
  middleName?: string;
  middleInitial?: string;
  title?: string;
  notes?: string;
  organizationName?: string;
  displayName?: string;
  hasDustCard?: boolean | null;
  dustCardNumber?: string;
  dustCardExpirationDate?: string;
  contactMethods?: ContactMethodCollection;
}

export interface PersonCreate {
  firstName?: string;
  preferredName?: string;
  lastName?: string;
  middleName?: string;
  title?: string;
  notes?: string;
  organizationName?: string;
  hasDustCard?: boolean | null;
  dustCardNumber?: string;
  dustCardExpirationDate?: string;
  contactMethods?: ContactMethod[];
}

/** Person edit model. `contactMethods` is the three repeater lists, not the API array. */
export interface PersonFormKit extends Omit<PersonCreate, "contactMethods">, EntityWithAuthRequestFields {
  id?: number;
  contactMethods: ContactMethodsFormkit;
}

export interface PersonQuery extends PagingQuery {
  keywords?: string;
  firstName?: string;
  lastName?: string;
  murn?: string;
}

export interface PersonElectronicForm {
  id: number;
  formMurn?: string;
  affiliationTypeMurn?: string;
  statusMurn?: string;
}

export interface PersonPermitVersion {
  id: number;
  permitId: number;
  versionId: number;
  version?: number;
  affiliationTypeMurn?: string;
  statusMurn?: string;
}

export function emptyPerson(): PersonFormKit {
  return {
    firstName: "",
    preferredName: "",
    lastName: "",
    title: "",
    organizationName: "",
    hasDustCard: false,
    dustCardNumber: "",
    dustCardExpirationDate: "",
    notes: "",
    contactMethods: contactMethodsToFormkit(undefined, personContactConfig),
  };
}

export function emptyContactMethod(kind: LE.ContactMethodType = LE.ContactMethodType.Email): ContactMethod {
  const method: ContactMethod = {
    kind,
    value: "",
    isPreferred: kind !== LE.ContactMethodType.Address,
    street: "",
    poBox: "",
    city: "",
    zipCode: "",
  };
  if (kind === LE.ContactMethodType.Address) {
    return contactMethodToFormkit(method);
  }
  return method;
}

export function contactMethodItems(collection?: ContactMethodCollection | null): ContactMethod[] {
  if (!collection) return [];
  if (Array.isArray(collection)) return collection;
  return collection._items ?? collection.value ?? [];
}

export function personDisplayName(
  person?: {
    displayName?: string;
    preferredName?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  } | null,
): string {
  if (!person) return "";
  if (person.displayName?.trim()) return person.displayName.trim();
  if (person.name?.trim()) return person.name.trim();
  const preferred = person.preferredName?.trim() || person.firstName?.trim() || "";
  const last = person.lastName?.trim() || "";
  return [preferred, last].filter(Boolean).join(" ");
}

export function inferredKind(method: ContactMethod): LE.ContactMethodType {
  const kind = String(method.kind ?? "");
  if (kind === LE.ContactMethodType.Email || kind === "email") return LE.ContactMethodType.Email;
  if (kind === LE.ContactMethodType.Phone || kind === "phone") return LE.ContactMethodType.Phone;
  if (kind === LE.ContactMethodType.Address || kind === "address") return LE.ContactMethodType.Address;
  const typeMurn = method.typeMurn ?? "";
  if ((Object.values(LE.ContactEmailType) as string[]).includes(typeMurn)) return LE.ContactMethodType.Email;
  if ((Object.values(LE.ContactPhoneType) as string[]).includes(typeMurn)) return LE.ContactMethodType.Phone;
  if ((Object.values(LE.ContactAddressType) as string[]).includes(typeMurn)) return LE.ContactMethodType.Address;
  if (typeMurn === LE.ContactMethodType.Email) return LE.ContactMethodType.Email;
  if (typeMurn === LE.ContactMethodType.Phone) return LE.ContactMethodType.Phone;
  if (typeMurn === LE.ContactMethodType.Address) return LE.ContactMethodType.Address;
  if (method.street || method.poBox || method.city || method.zipCode || method.murnAddress)
    return LE.ContactMethodType.Address;
  if ((method.value ?? "").includes("@")) return LE.ContactMethodType.Email;
  return LE.ContactMethodType.Phone;
}

export function normalizeContactMethod(method: ContactMethod): ContactMethod {
  return {
    ...method,
    kind: inferredKind(method),
  };
}

const childLookupGroup: Record<LE.ContactMethodType, "ContactEmailType" | "ContactPhoneType" | "ContactAddressType"> = {
  [LE.ContactMethodType.Email]: "ContactEmailType",
  [LE.ContactMethodType.Phone]: "ContactPhoneType",
  [LE.ContactMethodType.Address]: "ContactAddressType",
};

export function contactTypeGroupCode(
  kind: LE.ContactMethodType,
): "ContactPhoneType" | "ContactEmailType" | "ContactAddressType" {
  return childLookupGroup[kind];
}

export function preferredContactMethod(
  person?: { contactMethods?: ContactMethodCollection } | null,
  kind: LE.ContactMethodType = LE.ContactMethodType.Email,
): ContactMethod | undefined {
  const methods = contactMethodItems(person?.contactMethods).filter((method) => inferredKind(method) === kind);
  if (!methods.length) return undefined;
  return methods.find((method) => method.isPreferred) ?? methods[0];
}

export function preferredContactValue(
  person?: { contactMethods?: ContactMethodCollection } | null,
  kind: LE.ContactMethodType = LE.ContactMethodType.Email,
): string {
  const methods = contactMethodItems(person?.contactMethods).filter((method) => inferredKind(method) === kind);
  if (!methods.length) return "";
  const preferred = methods.find((method) => method.isPreferred) ?? methods[0];
  if (kind === LE.ContactMethodType.Address) {
    return (
      formatAddressFields(
        preferred.street,
        preferred.poBox,
        preferred.city,
        undefined,
        preferred.zipCode,
        undefined,
        true,
      ) ?? ""
    );
  }
  return preferred.value?.trim() ?? "";
}

/** Collapse flat address fields into the `murnAddress` group for AddressInputLookup. */
export function contactMethodToFormkit(method: ContactMethod): ContactMethod {
  const normalized = normalizeContactMethod(method);
  if (inferredKind(normalized) !== LE.ContactMethodType.Address) return normalized;

  const forAddress = { ...normalized } as ContactMethod & HasPlainAddressWithMurn;
  plainMurnAddressToFormkit(forAddress);
  return forAddress;
}

/** Flatten `murnAddress` back to FE flat fields (`poBox`, etc.). */
export function contactMethodFromFormkit(method: ContactMethod): ContactMethod {
  const normalized = normalizeContactMethod(method);
  if (inferredKind(normalized) !== LE.ContactMethodType.Address) {
    const { murnAddress: _murnAddress, ...rest } = normalized;
    return rest;
  }

  const forAddress = { ...normalized } as ContactMethod & HasPlainAddressWithMurn;
  if (forAddress.murnAddress) {
    plainMurnAddressFromFormkit(forAddress);
  }
  const { murnAddress: _murnAddress, ...rest } = forAddress;
  return rest;
}

export function personContactMethodsToFormkit(methods?: ContactMethod[] | null): ContactMethod[] {
  return (methods ?? []).map(contactMethodToFormkit);
}

export function personContactMethodsFromFormkit(methods?: ContactMethod[] | null): ContactMethod[] {
  return (methods ?? []).map(contactMethodFromFormkit);
}

function contactMethodApiKind(method: ContactMethod): "email" | "phone" | "address" {
  const kind = inferredKind(method);
  if (kind === LE.ContactMethodType.Email) return "email";
  if (kind === LE.ContactMethodType.Address) return "address";
  return "phone";
}

export function contactMethodsForSave(methods?: ContactMethod[] | null): ContactMethod[] {
  const prepared = personContactMethodsFromFormkit(methods).filter((method) => {
    if (!method.typeMurn) return false;
    const kind = inferredKind(method);
    if (kind === LE.ContactMethodType.Address)
      return !!(method.street || method.poBox || method.city || method.zipCode);
    return !!method.value?.trim();
  });
  const preferredKinds = new Set<string>();
  const singlePreferred = prepared.map((method) => {
    if (!method.isPreferred) return method;
    const kind = inferredKind(method);
    if (preferredKinds.has(kind)) return { ...method, isPreferred: false };
    preferredKinds.add(kind);
    return method;
  });
  return singlePreferred.map((method) => ({
    ...(typeof method.id === "number" && method.id > 0 ? { id: method.id } : {}),
    kind: contactMethodApiKind(method),
    typeMurn: method.typeMurn,
    isPreferred: !!method.isPreferred,
    value: method.value?.trim(),
    street: method.street,
    poBox: method.poBox,
    city: method.city,
    stateMurn: method.stateMurn,
    zipCode: method.zipCode,
    countryMurn: method.countryMurn,
  }));
}
